import { Fragment } from "react";
import {
  monthName,
  type CalendarEvent,
  type CalendarSystem,
  type CivilDate,
  type Lang,
} from "@calendar/shared";
import {
  dateKey,
  layoutEvents,
  sameDate,
  weekdayLabels,
  monthGrid,
  type PositionedEvent,
} from "../calendar-grid.js";
import { dayNumber, n, stamp } from "../format.js";

interface Props {
  days: CivilDate[];
  events: CalendarEvent[];
  anchor: CivilDate;
  today: CivilDate;
  timeZone: string;
  calendar: CalendarSystem;
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  onOpen: (id: string) => void;
  onCreate: (day: CivilDate) => void;
}

function Chip({ item, onOpen }: { item: PositionedEvent; onOpen: (id: string) => void }) {
  const { event } = item;
  const when = event.allDay
    ? ""
    : n(
        `${String(Math.floor(item.startMinutes / 60)).padStart(2, "0")}:${String(item.startMinutes % 60).padStart(2, "0")}`,
        "fa",
      );
  return (
    <div
      className={[
        "chip",
        item.allDay ? "chip--allday" : "",
        event.status === "done" ? "chip--done" : "",
        event.status === "cancelled" ? "chip--cancelled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        event.category
          ? ({ "--chip-color": event.category.color } as React.CSSProperties)
          : undefined
      }
      title={event.title}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(event.id);
      }}
    >
      {item.allDay ? "" : `${when} `}
      {event.title}
    </div>
  );
}

/**
 * Hover preview for a day cell. The month grid only has room for a few chips,
 * so this shows the rest on demand. Purely decorative and never focusable: the
 * cell itself stays the click target for creating an event.
 */
function DayPreview({
  items,
  lang,
  isToday,
}: {
  items: PositionedEvent[];
  lang: Lang;
  isToday: boolean;
}) {
  return (
    <div className="daypreview" role="tooltip">
      {isToday && <div className="daypreview__today">{lang === "fa" ? "امروز" : "Today"}</div>}
      {items.length === 0 ? (
        <div className="daypreview__empty">{lang === "fa" ? "رویدادی ثبت نشده" : "Nothing scheduled"}</div>
      ) : (
        <>
          {items.map((item) => {
            const when = item.event.allDay
              ? lang === "fa"
                ? "تمام روز"
                : "All day"
              : n(
                  `${String(Math.floor(item.startMinutes / 60)).padStart(2, "0")}:${String(item.startMinutes % 60).padStart(2, "0")}`,
                  lang,
                );
            const dot = item.event.category?.color ?? "var(--accent)";
            return (
              <div
                key={item.event.id}
                className={[
                  "daypreview__row",
                  item.event.status === "done" || item.event.status === "cancelled"
                    ? "daypreview__row--struck"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ "--dot": dot } as React.CSSProperties}
              >
                <span className="daypreview__time">{when}</span>
                <span className="daypreview__title">{item.event.title}</span>
              </div>
            );
          })}
          {items.length > 8 && (
            <div className="daypreview__more">
              {lang === "fa" ? `${n(items.length - 8, lang)} رویداد دیگر` : `${items.length - 8} more`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function MonthView(props: Props) {
  const { days, events, today, timeZone, calendar, lang, onOpen, onCreate } = props;
  const byDay = layoutEvents(events, days, timeZone, calendar);
  const labels = weekdayLabels(calendar, lang, days[0]!);
  const monthIndex = days.findIndex((d) => d.month === props.anchor.month && d.year === props.anchor.year);

  return (
    <div className="month" role="grid">
      {labels.map((label, i) => (
        <div className="month__head" key={i}>
          {label}
        </div>
      ))}
      {days.map((day, i) => {
        const list = byDay.get(dateKey(day)) ?? [];
        const inMonth = i >= monthIndex && i < monthIndex + 30;
        return (
          <div
            key={dateKey(day)}
            role="gridcell"
            className={[
              "month__cell",
              inMonth ? "" : "month__cell--muted",
              sameDate(day, today) ? "month__cell--today" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onCreate(day)}
          >
            <span className="month__daynum">{dayNumber(day, lang)}</span>
            {list.slice(0, 3).map((item) => (
              <Chip key={item.event.id} item={item} onOpen={onOpen} />
            ))}
            {list.length > 3 && <span className="muted">+{n(list.length - 3, lang)}</span>}
            <DayPreview items={list} lang={lang} isToday={sameDate(day, today)} />
          </div>
        );
      })}
    </div>
  );
}

const HOUR = 60;
const DAY_START = 6; // the grid shows 06:00 - 24:00
const HOURS = Array.from({ length: 24 - DAY_START }, (_, i) => DAY_START + i);

export function WeekView(props: Props) {
  const { days, events, today, timeZone, calendar, lang, t, onOpen, onCreate } = props;
  const byDay = layoutEvents(events, days, timeZone, calendar);
  const labels = weekdayLabels(calendar, lang, days[0]!);
  const todayKey = dateKey(today);

  return (
    <div className="week" role="grid">
      <div className="week__corner" />
      {days.map((day, i) => (
        <div
          className="week__head"
          key={dateKey(day)}
          style={sameDate(day, today) ? { color: "var(--accent)" } : undefined}
        >
          {labels[i]}
          <small>{dayNumber(day, lang)} {monthName(day.month, calendar, lang)}</small>
        </div>
      ))}

      {HOURS.map((hour) => (
        <Fragment key={hour}>
          <div className="week__gutter">{n(String(hour).padStart(2, "0"), lang)}:00</div>
          {days.map((day) => {
            const key = dateKey(day);
            const list = byDay.get(key) ?? [];
            // A block is drawn on the hour rows it spans, positioned from its
            // real start minute so a 10:30 meeting does not look like 10:00.
            const blocks = list.filter(
              (i) => !i.allDay && i.startMinutes < (hour + 1) * HOUR && i.endMinutes > hour * HOUR,
            );
            return (
              <div
                className="week__col"
                key={key + hour}
                onClick={() => onCreate(day)}
                style={key === todayKey ? { background: "var(--accent-soft)" } : undefined}
              >
                {blocks.map((item) => {
                  const top = ((item.startMinutes - hour * HOUR) / HOUR) * 100;
                  const height = ((Math.min(item.endMinutes, 24 * HOUR) - Math.max(item.startMinutes, hour * HOUR)) / HOUR) * 100;
                  return (
                    <div
                      key={item.event.id}
                      className="week__block"
                      style={{
                        top: `${Math.max(0, top)}%`,
                        height: `${Math.min(100 - Math.max(0, top), height)}%`,
                        ...(item.event.category
                          ? ({ "--chip-color": item.event.category.color } as React.CSSProperties)
                          : {}),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(item.event.id);
                      }}
                    >
                      {item.event.title}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Fragment>
      ))}

      {/* All-day row across the top, above the hour grid. */}
      <div className="week__corner">{t("field.allDay")}</div>
      {days.map((day) => {
        const list = (byDay.get(dateKey(day)) ?? []).filter((i) => i.allDay);
        return (
          <div className="week__col" key={"allday" + dateKey(day)} style={{ minHeight: 34 }}>
            {list.map((item) => (
              <Chip key={item.event.id} item={item} onOpen={onOpen} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function ListView(props: Props) {
  const { days, events, timeZone, calendar, lang, t, onOpen } = props;
  const byDay = layoutEvents(events, days, timeZone, calendar);

  const rows = days
    .map((day) => ({ day, items: byDay.get(dateKey(day)) ?? [] }))
    .filter((r) => r.items.length > 0);

  if (rows.length === 0) {
    return <p className="muted center">{t("empty.noEvents")}</p>;
  }

  return (
    <div className="list">
      {rows.map(({ day, items }) => (
        <div key={dateKey(day)}>
          <div className="list__day">
            {n(`${day.day} ${monthName(day.month, calendar, lang)}`, lang)}
          </div>
          {items.map((item) => {
            const s = item.allDay
              ? t("field.allDay")
              : stamp(item.event.startUtc, timeZone, calendar, lang).time;
            return (
              <div
                className="list__row"
                key={item.event.id}
                onClick={() => onOpen(item.event.id)}
              >
                <span className="list__time">{s}</span>
                <span>
                  <span className="list__title">{item.event.title}</span>
                  {item.event.location && <span className="muted"> · {item.event.location}</span>}
                  {item.event.notes && (
                    <div className="list__notes">{item.event.notes.slice(0, 140)}</div>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export { monthGrid };
